import React, { useState, useEffect, useCallback } from 'react';
import { Home } from 'lucide-react';
import sendPreInspectionEmail from './preInspectionEmailService';
import { validateFormCompletion, markFormCompleted, lookupVesselBySerial, isVesselScrapped, getScrappedFormMessage } from './formTraceabilityService';
import SerialValidationModal from './SerialValidationModal';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';
import { getCurrentUserDisplayName } from '../utils/currentUser';

const PreInspectionForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    inspectorName: '',
    date: new Date().toISOString().split('T')[0],
    serialNumber: '',
    manufacturer: '',
    waterCapacity: '',
    vesselOwner: '',
    typeOfInspectionDue: '',
    vesselPresentInInventory: 'no',
    vesselFreeFromCorrosion: 'no',
    vesselDoesNotContainManyways: 'no',
    vesselStructureIntact: 'no',
    vesselDataPlatePresent: 'no',
    noEvidenceOfContamination: 'no',
    liftingLugsPresent: 'no',
    vesselFreeFromDents: 'no',
    vesselInspectionDateDue: 'no',
    additionalComments: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [serialModalMessage, setSerialModalMessage] = useState('');
  const [serialLookupState, setSerialLookupState] = useState({ loading: false, notFound: false, lastLookup: '' });
  const [missingVesselFields, setMissingVesselFields] = useState({});
  const MISSING_PLACEHOLDER = 'Unable to obtain — please enter';
  const closeSerialModal = useCallback(() => setSerialModalOpen(false), []);

  useEffect(() => {
    const name = getCurrentUserDisplayName();
    if (!name) return;
    setFormData(prev => prev.inspectorName ? prev : { ...prev, inspectorName: name });
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'serialNumber' && serialLookupState.lastLookup) {
      setSerialLookupState({ loading: false, notFound: false, lastLookup: '' });
      setMissingVesselFields({});
    }
  };

  const handleSerialLookup = useCallback(async () => {
    const serial = String(formData.serialNumber || '').trim();
    if (!serial || serial === serialLookupState.lastLookup) return;

    setSerialLookupState({ loading: true, notFound: false, lastLookup: serial });
    try {
      const result = await lookupVesselBySerial(serial);
      if (!result.found) {
        setSerialLookupState({ loading: false, notFound: true, lastLookup: serial });
        setMissingVesselFields({});
        return;
      }

      const vessel = result.vessel || {};
      const missing = {};
      const hasManufacturer = !!vessel.vesselManufacturer;
      const hasOwner = !!vessel.owner;
      const hasVesselSize = vessel.vesselSize !== null && vessel.vesselSize !== undefined && vessel.vesselSize !== '';

      if (!hasManufacturer) missing.manufacturer = true;
      if (!hasOwner) missing.vesselOwner = true;
      if (!hasVesselSize) missing.waterCapacity = true;

      setFormData(prev => {
        const next = { ...prev };
        if (!prev.manufacturer && hasManufacturer) {
          next.manufacturer = vessel.vesselManufacturer;
        }
        if (!prev.vesselOwner && hasOwner) {
          next.vesselOwner = vessel.owner;
        }
        if ((prev.waterCapacity === '' || prev.waterCapacity === null || prev.waterCapacity === undefined)
            && hasVesselSize) {
          next.waterCapacity = String(vessel.vesselSize);
        }
        return next;
      });
      setMissingVesselFields(missing);
      setSerialLookupState({ loading: false, notFound: false, scrapped: isVesselScrapped(vessel), lastLookup: serial });
    } catch (error) {
      console.warn('Vessel lookup failed:', error);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
    }
  }, [formData.serialNumber, serialLookupState.lastLookup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const mandatoryFields = {
      inspectorName: 'Inspector Name',
      date: 'Date',
      serialNumber: 'Serial Number',
      manufacturer: 'Manufacturer',
      waterCapacity: 'Water Capacity',
      vesselOwner: 'Vessel Owner',
      typeOfInspectionDue: 'Type Of Inspection Due'
    };
    
    const missingFields = [];
    for (const [field, label] of Object.entries(mandatoryFields)) {
      if (!formData[field] || formData[field].trim() === '') {
        missingFields.push(label);
      }
    }
    
    if (missingFields.length > 0) {
      setSubmitMessage(`Please fill in the following mandatory fields: ${missingFields.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    const inspectionItems = {
      vesselPresentInInventory: 'Vessel Present In Inventory',
      vesselFreeFromCorrosion: 'Vessel Free from Excessive Corrosion',
      vesselDoesNotContainManyways: 'Vessel Does Not Contain Manyways',
      vesselStructureIntact: 'Vessel Structure Intact',
      vesselDataPlatePresent: 'Vessel Data Plate Present and Legible',
      noEvidenceOfContamination: 'No Evidence of Contamination',
      liftingLugsPresent: 'Lifting Lugs Present and No Evidence of Defects',
      vesselFreeFromDents: 'Vessel Free from Dents and Bulges',
      vesselInspectionDateDue: 'Vessel Inspection Date Due'
    };
    
    const failedInspections = [];
    for (const [field, label] of Object.entries(inspectionItems)) {
      if (formData[field] === 'no') {
        failedInspections.push(label);
      }
    }
    
    if (failedInspections.length > 0) {
      setSubmitMessage(
        `⚠️ INSPECTION FAILED - The following items were marked as "No": ${failedInspections.join(', ')}. ` +
        `This form cannot be submitted. Please contact a senior inspector immediately and fill out a fail sheet if the issue cannot be remedied.`
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await validateFormCompletion(formData.serialNumber, 'preInspection');
    } catch (error) {
      console.error('Serial validation failed:', error);
      setSerialModalMessage(error.message || 'Vessel Serial Number not found in Vessel Inventory.');
      setSerialModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    try {
      await sendPreInspectionEmail(formData);
      await markFormCompleted(formData.serialNumber, 'preInspection', formData.date, { completedBy: formData.inspectorName });
      setFormData({
        inspectorName: '',
        date: new Date().toISOString().split('T')[0],
        serialNumber: '',
        manufacturer: '',
        waterCapacity: '',
        vesselOwner: '',
        typeOfInspectionDue: '',
        vesselPresentInInventory: 'no',
        vesselFreeFromCorrosion: 'no',
        vesselDoesNotContainManyways: 'no',
        vesselStructureIntact: 'no',
        vesselDataPlatePresent: 'no',
        noEvidenceOfContamination: 'no',
        liftingLugsPresent: 'no',
        vesselFreeFromDents: 'no',
        vesselInspectionDateDue: 'no',
        additionalComments: ''
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting pre inspection form:', error);
      setSubmitMessage(error.message || 'Error submitting form. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Pre Inspection Form</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-PIF-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Vessel Pre-Inspection Documentation</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Submit Message at Top */}
            {submitMessage && (
              <div className={`p-5 rounded-lg border-2 shadow-md ${
                submitMessage.includes('INSPECTION FAILED')
                  ? 'bg-red-900/30 text-red-400 border-red-500'
                  : submitMessage.includes('Error') || submitMessage.includes('Please fill') || submitMessage.includes('Please set') || submitMessage.includes('not found') || submitMessage.includes('Unable to validate')
                  ? 'bg-red-900/30 text-red-400 border-red-500/30' 
                  : 'bg-green-900/30 text-pvs-accent border-green-500/30'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {submitMessage.includes('INSPECTION FAILED') ? (
                      <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : submitMessage.includes('Error') || submitMessage.includes('Please fill') || submitMessage.includes('Please set') || submitMessage.includes('not found') || submitMessage.includes('Unable to validate') ? (
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className={`${submitMessage.includes('INSPECTION FAILED') ? 'text-base font-bold' : 'text-sm font-medium'}`}>
                      {submitMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Basic Information Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Inspector Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspector Name *</label>
                  <input type="text" name="inspectorName" value={formData.inspectorName} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date *</label>
                  <input type="date" name="date" value={formData.date} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
              </div>
            </div>

            {/* Vessel Information Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Vessel Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Serial Number *</label>
                  <input type="text" name="serialNumber" value={formData.serialNumber} onChange={handleInputChange} onBlur={handleSerialLookup} className="block w-full pvs-input" required />
                  {serialLookupState.loading && (
                    <p className="mt-1 text-xs text-pvs-text-dim">Looking up vessel…</p>
                  )}
                  {!serialLookupState.loading && serialLookupState.notFound && (
                    <p className="mt-1 text-xs text-red-400">Serial number not found in Vessel Inventory.</p>
                  )}
                  {!serialLookupState.loading && serialLookupState.scrapped && (
                    <p className="mt-1 text-xs text-red-400">{getScrappedFormMessage('preInspection')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Manufacturer *</label>
                  <input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleInputChange} placeholder={missingVesselFields.manufacturer ? MISSING_PLACEHOLDER : ''} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Water Capacity *</label>
                  <input type="text" name="waterCapacity" value={formData.waterCapacity} onChange={handleInputChange} placeholder={missingVesselFields.waterCapacity ? MISSING_PLACEHOLDER : ''} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Vessel Owner *</label>
                  <input type="text" name="vesselOwner" value={formData.vesselOwner} onChange={handleInputChange} placeholder={missingVesselFields.vesselOwner ? MISSING_PLACEHOLDER : ''} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Type Of Inspection Due *</label>
                  <select name="typeOfInspectionDue" value={formData.typeOfInspectionDue} onChange={handleInputChange} className="block w-full pvs-select" required>
                    <option value="">Select inspection type</option>
                    <option value="In Service">In Service</option>
                    <option value="Thorough">Thorough</option>
                    <option value="Enhanced">Enhanced</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Inspection Checklist */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Inspection Checklist</h2>
              <div className="space-y-4">
                {[
                  { field: 'vesselPresentInInventory', label: 'Vessel Present In Inventory *' },
                  { field: 'vesselFreeFromCorrosion', label: 'Vessel Free from Excessive Corrosion *' },
                  { field: 'vesselDoesNotContainManyways', label: 'Vessel Does Not Contain Manyways *' },
                  { field: 'vesselStructureIntact', label: 'Vessel Structure Intact *' },
                  { field: 'vesselDataPlatePresent', label: 'Vessel Data Plate Present and Legible *' },
                  { field: 'noEvidenceOfContamination', label: 'No Evidence of Contamination *' },
                  { field: 'liftingLugsPresent', label: 'Lifting Lugs Present and No Evidence of Defects *' },
                  { field: 'vesselFreeFromDents', label: 'Vessel Free from Dents and Bulges *' },
                  { field: 'vesselInspectionDateDue', label: 'Vessel Inspection Date Due *' }
                ].map(({ field, label }) => (
                  <div key={field} className="flex items-center justify-between">
                    <label className="text-sm font-medium text-pvs-text-muted">{label}</label>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${formData[field] === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, [field]: formData[field] === 'yes' ? 'no' : 'yes' }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:ring-offset-2 ${
                          formData[field] === 'yes' ? 'bg-green-600' : 'bg-red-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData[field] === 'yes' ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                      <span className={`text-sm font-medium ${formData[field] === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Comments Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Additional Comments</h2>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspector Additional Comments</label>
                <textarea name="additionalComments" value={formData.additionalComments} onChange={handleInputChange} rows={4} className="block w-full pvs-input" placeholder="Enter any additional comments or observations..." />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
              >
                <Home className="h-4 w-4" />
                Back to Forms
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Pre Inspection Form'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <SerialValidationModal isOpen={serialModalOpen} message={serialModalMessage} onClose={closeSerialModal} />
      <FormSuccessModal isOpen={showSuccessModal} formName="Pre Inspection Form" onClose={onBack} />
    </PVSPageLayout>
  );
};

export default PreInspectionForm;
